<template>
  <div
    id="hero-search-refresh-v2"
    ref="mainContainer"
    class="hero-search-refresh-v2 justify-content-space-between tw-relative tw-z-30 tw-flex tw-flex-col tw-gap-3 md:tw-mx-auto md:tw-w-[684px] md:tw-flex-row md:tw-gap-0"
  >
    <div
      data-uh="select-city-filter"
      class="select-city home-find-uni-cities-input tw-relative tw-rounded-full"
      :class="{
        'text-red': locationError,
        'input-focused': isLocationInputFocused,
        'md:tw-w-[341px]': showBedroomSelector,
        'md:tw-flex-grow': !showBedroomSelector,
      }"
      data-testid="select-city-filter"
    >
      <div class="tw-relative">
        <label
          id="location-select-2025"
          class="hero-search-label-v2"
          :class="{ 'label-focused': isLocationInputFocused }"
          for="home-find-locations-2025"
        >
          Location
        </label>
        <div
          class="tw-relative tw-pl-[16px]"
          data-testid="location-select-container"
        >
          <input
            id="home-find-locations-2025"
            ref="locationInput"
            v-model="searchQuery"
            name="home-find-locations-2025"
            :placeholder="locationPlaceholder"
            class="tw-w-full tw-rounded-sm tw-border-none tw-bg-transparent tw-p-[8px] tw-text-[16px] tw-font-normal tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-transparent focus-visible:tw-ring-offset-1"
            type="text"
            role="combobox"
            aria-autocomplete="list"
            aria-owns="location-search-results"
            aria-expanded="showLocationDropdown"
            aria-haspopup="listbox"
            aria-activedescendant="location-option-highlighted"
            data-testid="location-input"
            @focus="handleLocationFocus"
            @blur="handleLocationBlur"
            @keydown.down="(e) => handleLocationKeyNavigation('down', e)"
            @keydown.up="(e) => handleLocationKeyNavigation('up', e)"
            @keydown.right="(e) => handleLocationKeyNavigation('right', e)"
            @keydown.left="(e) => handleLocationKeyNavigation('left', e)"
            @keydown.enter="selectHighlightedLocation"
            @keydown.esc="closeLocationDropdown"
          />
          <button
            v-if="searchQuery"
            class="tw-absolute tw-right-[24px] tw-top-1/2 -tw-translate-y-1/2 tw-transform tw-text-gray-400 hover:tw-text-gray-600"
            type="button"
            @click="clearLocationSearch($event)"
          >
            <span class="tw-sr-only">Clear search</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="tw-h-5 tw-w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="showBedroomSelector"
      class="tw-m-[8px] tw-h-[calc(100%-16px)] tw-w-[1px] tw-bg-[#D1D5DB]"
    ></div>

    <div
      v-if="showBedroomSelector"
      data-uh="select-bedrooms-filter"
      class="select-bedrooms tw-relative tw-rounded-full md:tw-w-[274px]"
      data-testid="search-select-bedroom-filter"
    >
      <label
        id="bedrooms-select-2025"
        class="hero-search-label-v2"
        for="home-find-bedrooms-2025"
        @mousedown.prevent="showBedroomsDropdown = true"
      >
        How many bedrooms?
      </label>
      <div class="tw-relative">
        <div
          id="home-find-bedrooms-2025"
          ref="bedroomsInput"
          class="uh-sans-choices-select-display tw-flex tw-h-[40px] tw-w-full tw-cursor-pointer tw-items-center tw-border-none tw-bg-transparent tw-p-[8px] tw-pl-[24px] tw-text-[16px] tw-font-normal tw-outline-none"
          tabindex="0"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded="showBedroomsDropdown"
          aria-labelledby="bedrooms-select-2025"
          aria-controls="bedrooms-listbox"
          aria-activedescendant="bedroom-option-highlighted"
          @mousedown.prevent="handleBedroomsMouseDown"
          @focus="handleBedroomsFocus"
          @blur="handleBedroomsBlur"
          @keydown.down="(e) => handleBedroomsKeyNavigation('down', e)"
          @keydown.up="(e) => handleBedroomsKeyNavigation('up', e)"
          @keydown.right="(e) => handleBedroomsKeyNavigation('down', e)"
          @keydown.left="(e) => handleBedroomsKeyNavigation('up', e)"
          @keydown.enter="selectHighlightedBedroom"
          @keydown.esc="closeBedroomsDropdown"
        >
          {{ selectedBedroomText }}
        </div>
      </div>
      <div
        v-if="showBedroomsDropdown"
        id="bedrooms-listbox"
        role="listbox"
        aria-labelledby="bedrooms-select-2025"
        class="tw-absolute tw-z-50 tw-mt-[10px] tw-max-h-80 tw-w-full tw-overflow-y-auto tw-overscroll-contain tw-rounded-[24px] tw-bg-white tw-shadow-lg tw-no-scrollbar focus-visible:tw-outline-none focus-visible:tw-ring-0 focus-visible:tw-ring-transparent"
        data-testid="bedrooms-select-dropdown"
      >
        <button
          v-for="(bedroom, index) in bedroomOptions"
          :id="
            highlightedBedroomIndex === index
              ? 'bedroom-option-highlighted'
              : `bedroom-option-${index}`
          "
          :key="`bedroom-${bedroom.id}`"
          type="button"
          role="option"
          tabindex="-1"
          data-testid="bedrooms-select-item"
          :aria-selected="highlightedBedroomIndex === index"
          class="tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-border-b tw-border-[#E5E7EB] tw-bg-transparent tw-px-6 tw-py-[16px] tw-text-left tw-text-[14px] tw-font-normal hover:tw-bg-gray-100"
          :class="[
            highlightedBedroomIndex === index ? 'tw-bg-gray-100' : '',
            isKeyboardNavigation ? 'keyboard-navigating' : '',
          ]"
          @click="selectBedroom(bedroom)"
          @mouseover="handleBedroomMouseOver(index)"
        >
          <span
            class="tw-z-10 tw-border tw-border-transparent"
            :class="{
              'tw-border-black':
                highlightedBedroomIndex === index && isKeyboardNavigation,
            }"
          >
            {{ bedroom.name }}
            <span
              v-if="
                bedroom.id !== '' &&
                selectedLocation &&
                showPropertyCounts &&
                !selectedLocation.is_fallback_data
              "
              class="tw-text-sm tw-text-gray-400"
            >
              ({{ bedroom.count }})
            </span>
          </span>
        </button>
      </div>
    </div>

    <button
      id="loadSearch-2025"
      class="search-icon tw-block tw-max-h-[65px] tw-min-h-[50px] tw-w-full tw-rounded-full tw-bg-refreshPrimaryBg-light tw-text-center tw-text-[16px] tw-font-semibold tw-text-white tw-duration-200 hover:tw-bg-refreshPrimaryBg-dark focus-visible:tw-ring-2 focus-visible:tw-ring-[#000] focus-visible:tw-ring-offset-1 md:tw-ml-1 md:tw-max-w-[65px]"
      type="button"
      data-uh="search-homes-cta"
      data-testid="search-homes-cta"
      @click="search"
    >
      <span class="sr-only-desktop">Find homes</span>
    </button>

    <div
      v-if="showLocationDropdown"
      ref="locationDropdown"
      class="location-dropdown-fullwidth tw-absolute tw-z-50 tw-max-h-[440px] tw-overflow-y-auto tw-overflow-x-hidden tw-overscroll-contain tw-rounded-[24px] tw-bg-white tw-shadow-lg tw-no-scrollbar"
      role="listbox"
      aria-labelledby="location-select-2025"
      data-testid="location-select-dropdown"
    >
      <div
        v-if="filteredLocations.length > 0"
        class="filtered-results tw-relative"
      >
        <h3
          v-if="!hasQuery"
          class="tw-sticky tw-top-0 tw-z-[200] tw-m-0 tw-w-full tw-bg-white tw-px-[24px] tw-py-[16px] tw-pb-[8px] tw-text-[16px] tw-font-[800]"
        >
          All cities
        </h3>
        <template v-if="!hasQuery">
          <button
            v-for="(location, index) in sortedFilteredCities"
            :id="
              highlightedLocationIndex === index
                ? 'location-option-highlighted'
                : `location-option-${index}`
            "
            :key="`location-${locationIdentity(location)}`"
            type="button"
            role="option"
            tabindex="0"
            data-testid="location-select-item"
            :aria-selected="highlightedLocationIndex === index"
            :data-location-type="location.type.toLowerCase()"
            data-location-list="all-cities"
            class="tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-p-[16px] tw-text-left hover:tw-bg-gray-100"
            :class="[
              'location-results',
              highlightedLocationIndex === index && !showingInitialSuggestions
                ? 'tw-bg-gray-100'
                : '',
              !hasQuery ? 'tw-py-[4px]' : 'tw-py-[8px]',
              isKeyboardNavigation ? 'keyboard-navigating' : '',
            ]"
            @click="
              selectLocation(location);
              isKeyboardNavigation = false;
            "
            @mousedown="isKeyboardNavigation = false"
            @mouseover="handleMouseOver(index)"
          >
            <div
              class="location-results-inner tw-flex tw-items-center"
            >
              <span
                class="tw-border-[1px] tw-border-transparent tw-text-[14px] tw-font-[400]"
              >
                <span>
                  <span
                    v-for="(part, i) in getHighlightedParts(
                      location.name,
                      searchQuery
                    )"
                    :key="i"
                    :class="part.highlight ? 'tw-font-bold' : ''"
                    >{{ part.text }}</span
                  >
                </span>
                <span
                  v-if="showPropertyCounts && !location.is_fallback_data"
                  class="tw-text-[14px] tw-text-gray-400"
                >
                  ({{ getLocationPropertyCount(location) }})
                </span>
              </span>
            </div>
            <span
              v-if="hasQuery"
              class="tw-text-xs tw-capitalize tw-text-gray-500"
            >
              {{ location.type }}
            </span>
          </button>

          <h3
            class="tw-sticky tw-top-0 tw-z-[200] tw-m-0 tw-w-full tw-bg-white tw-px-[24px] tw-py-[16px] tw-pb-[8px] tw-text-[16px] tw-font-[800]"
          >
            All universities
          </h3>

          <button
            v-for="(location, index) in sortedFilteredUniversities"
            :id="
              highlightedLocationIndex === index + sortedFilteredCities.length
                ? 'location-option-highlighted'
                : `location-option-${index + sortedFilteredCities.length}`
            "
            :key="`location-${locationIdentity(location)}`"
            type="button"
            role="option"
            tabindex="0"
            data-testid="location-select-item"
            :aria-selected="
              highlightedLocationIndex === index + sortedFilteredCities.length
            "
            :data-location-type="location.type.toLowerCase()"
            class="tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-p-[16px] tw-text-left hover:tw-bg-gray-100"
            :class="[
              'location-results',
              highlightedLocationIndex ===
                index + sortedFilteredCities.length &&
              !showingInitialSuggestions
                ? 'tw-bg-gray-100'
                : '',
              !hasQuery ? 'tw-py-[4px]' : 'tw-py-[8px]',
              isKeyboardNavigation ? 'keyboard-navigating' : '',
            ]"
            @click="
              selectLocation(location);
              isKeyboardNavigation = false;
            "
            @mousedown="isKeyboardNavigation = false"
            @mouseover="handleMouseOver(index + sortedFilteredCities.length)"
          >
            <div
              class="location-results-inner tw-flex tw-items-center"
            >
              <span
                class="tw-border-[1px] tw-border-transparent tw-text-[14px] tw-font-[400]"
              >
                <span>
                  <span
                    v-for="(part, i) in getHighlightedParts(
                      location.name,
                      searchQuery
                    )"
                    :key="i"
                    :class="part.highlight ? 'tw-font-bold' : ''"
                    >{{ part.text }}</span
                  >
                </span>
                <span
                  v-if="showPropertyCounts && !location.is_fallback_data"
                  class="tw-text-sm tw-text-gray-400"
                >
                  ({{ getLocationPropertyCount(location) }})
                </span>
              </span>
            </div>
            <span
              v-if="hasQuery"
              class="tw-text-xs tw-capitalize tw-text-gray-500"
            >
              {{ location.type }}
            </span>
          </button>
        </template>

        <template v-else>
          <button
            v-for="(location, index) in sortedFilteredLocations"
            :id="
              highlightedLocationIndex === index
                ? 'location-option-highlighted'
                : `location-option-${index}`
            "
            :key="`location-${locationIdentity(location)}`"
            type="button"
            role="option"
            tabindex="0"
            data-testid="location-select-item"
            :aria-selected="highlightedLocationIndex === index"
            :data-location-type="location.type.toLowerCase()"
            class="tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-p-[16px] tw-text-left hover:tw-bg-gray-100"
            :class="[
              'location-results',
              highlightedLocationIndex === index && !showingInitialSuggestions
                ? 'tw-bg-gray-100'
                : '',
              !hasQuery ? 'tw-py-[4px]' : 'tw-py-[8px]',
              isKeyboardNavigation ? 'keyboard-navigating' : '',
            ]"
            @click="
              selectLocation(location);
              isKeyboardNavigation = false;
            "
            @mousedown="isKeyboardNavigation = false"
            @mouseover="handleMouseOver(index)"
          >
            <div
              class="location-results-inner tw-flex tw-items-center"
            >
              <span
                class="tw-border-[1px] tw-border-transparent tw-text-[14px] tw-font-[400]"
              >
                <span>
                  <span
                    v-for="(part, i) in getHighlightedParts(
                      location.name,
                      searchQuery
                    )"
                    :key="i"
                    :class="part.highlight ? 'tw-font-bold' : ''"
                    >{{ part.text }}</span
                  >
                </span>
                <span
                  v-if="showPropertyCounts && !location.is_fallback_data"
                  class="tw-text-sm tw-text-gray-400"
                >
                  ({{ getLocationPropertyCount(location) }})
                </span>
              </span>
            </div>
            <span
              v-if="hasQuery"
              class="tw-text-xs tw-capitalize tw-text-gray-500"
            >
              {{ location.type }}
            </span>
          </button>
        </template>
      </div>

      <div
        v-else
        class="tw-px-4 tw-py-3 tw-text-[14px] tw-text-gray-500"
      >
        <template v-if="searchQuery && searchQuery.length >= 2">
          {{
            selectedBedroom && selectedBedroom.id
              ? `No locations found with ${selectedBedroomText.toLowerCase()} matching "${searchQuery}"`
              : `No locations found matching "${searchQuery}"`
          }}
        </template>
        <template v-else-if="searchQuery && searchQuery.length < 2">
          Please enter at least 2 characters to search
        </template>
        <template v-else>
          No locations found with {{ selectedBedroomText.toLowerCase() }}
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import Fuse from 'fuse.js';

export default {
  name: 'PropertiesSearch2025',

  props: {
    bedroomsData: { type: Array, default: () => [] },
    slug: { type: String, default: 'home' },
    enableSpareRoomFunctionality: { type: Boolean, default: false },
  },
  data() {
    return {
      searchQuery: '',
      hasQuery: false,
      showLocationDropdown: false,
      showBedroomsDropdown: false,
      isLocationInputFocused: false,
      isKeyboardNavigation: false,
      lastInteractionWasKeyboard: false,
      highlightedLocationIndex: -1,
      highlightedBedroomIndex: -1,
      selectedLocation: null,
      selectedBedroom: null,
      selectedBedroomText: 'Any',
      locationPlaceholder: 'Search for a city or nearby university',
      locationError: false,
      showingInitialSuggestions: false,
      mouseOverIndex: -1,
      mouseOverBedroomIndex: -1,
      propertyCountsData: [],
      fuse: null,
      baseBedroomOptions: [
        { id: '', name: 'Any', count: 0 },
        { id: '1', name: '1 Bedroom', count: 0 },
        { id: '2', name: '2 Bedrooms', count: 0 },
        { id: '3', name: '3 Bedrooms', count: 0 },
        { id: '4', name: '4 Bedrooms', count: 0 },
        { id: '5', name: '5 Bedrooms', count: 0 },
        { id: '6', name: '6 Bedrooms', count: 0 },
        { id: '7', name: '7 Bedrooms', count: 0 },
        { id: '8', name: '8 Bedrooms', count: 0 },
        { id: '9', name: '9 Bedrooms', count: 0 },
        { id: '10-plus', name: '10+ Bedrooms', count: 0 },
      ],
      lastMouseOverTarget: { type: null, index: -1 },
    };
  },
  computed: {
    showPropertyCounts() {
      if (['private-halls', 'spare-rooms'].includes(this.slug)) return true;

      return typeof window.__HERO_V2_SHOW_PROPERTY_COUNTS === 'undefined'
        ? true
        : !!window.__HERO_V2_SHOW_PROPERTY_COUNTS;
    },
    filteredLocations() {
      if (!this.searchQuery || this.searchQuery.length < 2)
        return this.filterLocationsByBedroom(this.propertyCountsData);

      try {
        const query = this.searchQuery.toLowerCase().trim();

        // Performance optimization: limit Fuse.js results for large searches
        const maxResults = 10; // Fewer results for short queries like "Uni"
        const searchResults = this.fuse.search(query, { limit: maxResults });

        // Performance optimization: use Set for O(1) deduplication instead of array.some()
        const seenLocations = new Set();
        const allMatches = [];

        // First pass: word beginning matches (higher priority)
        for (const r of searchResults) {
          if (!r.item || !r.item.name || !r.item.slug) continue;

          const matchesWordBeginning = r.item.name
            .toLowerCase()
            .split(/\s+/)
            .some((word) => word.startsWith(query));
          const locationKey = this.locationIdentity(r.item);

          if (matchesWordBeginning && !seenLocations.has(locationKey)) {
            seenLocations.add(locationKey);
            allMatches.push(r.item);
          }
        }

        // Second pass: substring matches (lower priority, only if not already added)
        for (const r of searchResults) {
          if (!r.item || !r.item.name || !r.item.slug) continue;
          const locationKey = this.locationIdentity(r.item);

          if (
            !seenLocations.has(locationKey) &&
            r.item.name.toLowerCase().includes(query)
          ) {
            seenLocations.add(locationKey);
            allMatches.push(r.item);
          }
        }

        // Performance optimization: limit final results to prevent UI freeze
        const limitedMatches = allMatches.slice(0, 150); // Cap at 150 results max

        return this.filterLocationsByBedroom(limitedMatches);
      } catch (_error) {
        // Fallback to a scrollable data set containing all locations
        return this.filterLocationsByBedroom(this.propertyCountsData);
      }
    },
    sortedFilteredLocations() {
      try {
        // If there is no search query, then only show cities
        if (!this.hasQuery) {
          return this.filteredLocations
            .filter((l) => l.type == 'city')
            .sort((a, b) => {
              // Bulletproof name comparison
              const aName = a && a.name ? String(a.name) : '';
              const bName = b && b.name ? String(b.name) : '';

              return aName.localeCompare(bName);
            });
        }

        // If searching, preserve Fuse.js relevance order (don't sort alphabetically)
        if (this.searchQuery && this.searchQuery.length >= 2) {
          return this.filteredLocations;
        }

        // If not searching, sort alphabetically for browsing
        return [...this.filteredLocations].sort((a, b) => {
          // Bulletproof name comparison
          const aName = a && a.name ? String(a.name) : '';
          const bName = b && b.name ? String(b.name) : '';

          return aName.localeCompare(bName);
        });
      } catch (error) {
        // Silent fallback to prevent Vue reactivity crashes
        return [];
      }
    },
    sortedFilteredCities() {
      try {
        if (this.hasQuery) return [];
        return this.filteredLocations
          .filter((l) => l.type == 'city')
          .sort((a, b) => {
            const aName = a && a.name ? String(a.name) : '';
            const bName = b && b.name ? String(b.name) : '';

            return aName.localeCompare(bName);
          });
      } catch (_error) {
        return [];
      }
    },
    sortedFilteredUniversities() {
      try {
        if (this.hasQuery) return [];
        return this.filteredLocations
          .filter((l) => l.type == 'university')
          .sort((a, b) => {
            const aName = a && a.name ? String(a.name) : '';
            const bName = b && b.name ? String(b.name) : '';

            return aName.localeCompare(bName);
          });
      } catch (_error) {
        return [];
      }
    },
    navigableLocations() {
      if (!this.hasQuery) {
        return [
          ...this.sortedFilteredCities,
          ...this.sortedFilteredUniversities,
        ];
      }

      return this.sortedFilteredLocations;
    },
    bedroomOptions() {
      let baseOptions = this.baseBedroomOptions;

      // Add "Spare rooms" option when feature flag is enabled
      if (this.enableSpareRoomFunctionality) {
        baseOptions = [
          this.baseBedroomOptions[0], // "Any"
          { id: 'spare-rooms', name: 'Spare Rooms', count: 0 },
          ...this.baseBedroomOptions.slice(1), // Rest of the options starting from "1 Bedroom"
        ];
      }

      if (!this.selectedLocation)
        return baseOptions.map((option) => ({
          ...option,
          count: 0,
        }));

      const options = baseOptions.map((option) => {
        try {
          // Handle spare rooms option separately
          if (option.id === 'spare-rooms') {
            const spareRoomsCount =
              this.selectedLocation &&
              this.selectedLocation.by_bedrooms &&
              typeof this.selectedLocation.by_bedrooms === 'object' &&
              typeof this.selectedLocation.by_bedrooms.spare_rooms === 'number'
                ? this.selectedLocation.by_bedrooms.spare_rooms
                : 0;

            return { ...option, count: spareRoomsCount };
          }

          const count =
            option.id === ''
              ? this.selectedLocation &&
                typeof this.selectedLocation.total === 'number'
                ? this.selectedLocation.total
                : 0
              : this.selectedLocation &&
                this.selectedLocation.by_bedrooms &&
                typeof this.selectedLocation.by_bedrooms === 'object' &&
                typeof this.selectedLocation.by_bedrooms[option.id] === 'number'
              ? this.selectedLocation.by_bedrooms[option.id]
              : 0;
          return { ...option, count };
        } catch (error) {
          return { ...option, count: 0 };
        }
      });

      // Only filter zero counts when a location is selected and not using fallback data
      // Don't filter out spare rooms option even if count is 0
      if (
        this.selectedLocation &&
        !this.selectedLocation.is_fallback_data &&
        typeof window.__HERO_V2_HIDE_ZERO_COUNT_BEDROOMS !== 'undefined' &&
        window.__HERO_V2_HIDE_ZERO_COUNT_BEDROOMS
      ) {
        return options.filter(
          (option) =>
            option.id === '' || option.id === 'spare-rooms' || option.count > 0
        );
      }
      return options;
    },
    showBedroomSelector() {
      return !['private-halls', 'spare-rooms'].includes(this.slug);
    },
  },
  created() {
    // Validate and clean the property counts data
    const rawData = window.__PROPERTY_COUNTS || [];
    this.propertyCountsData = rawData.filter(
      (item) =>
        item &&
        typeof item === 'object' &&
        item.name &&
        typeof item.name === 'string' &&
        item.slug &&
        typeof item.total !== 'undefined'
    );

    // Initialize Fuse with validated data
    try {
      this.fuse = new Fuse(this.propertyCountsData, {
        keys: ['name'],
        threshold: 0.4,
        minMatchCharLength: 2,
      });
    } catch (error) {
      // Fallback: create empty Fuse instance
      this.fuse = new Fuse([], {
        keys: ['name'],
        threshold: 0.4,
        minMatchCharLength: 2,
      });
    }
  },
  mounted() {
    // Removes the search placeholder that displays while vue is mounting.
    const placeholder = document.getElementById('PropertiesSearchPlaceholder');

    if (placeholder) {
      placeholder.style.display = 'none';
    }

    this.setupEventListeners();

    this.$watch('searchQuery', (newQuery) => {
      const hasQuery = newQuery.length > 0 && newQuery.length >= 2;

      this.hasQuery = hasQuery;
      this.showingInitialSuggestions = false;
    });
  },
  beforeDestroy() {
    this.cleanupEventListeners();
  },
  methods: {
    locationIdentity(location) {
      const type = String(location.type || '').toLowerCase();
      const city = type === 'university' ? location.city || '' : '';

      return `${type}:${city}:${location.slug}`;
    },
    setupEventListeners() {
      this.setupDropdownWatchers();

      this._mousedownListener = () => {
        this.isKeyboardNavigation = false;
        this.lastInteractionWasKeyboard = false;
      };

      this._keydownListener = (e) => {
        const navKeys = [
          'Tab',
          'ArrowDown',
          'ArrowUp',
          'ArrowLeft',
          'ArrowRight',
          'Enter',
          ' ',
        ];
        if (navKeys.includes(e.key)) {
          this.lastInteractionWasKeyboard = true;
        }
      };

      document.addEventListener('mousedown', this._mousedownListener);
      document.addEventListener('keydown', this._keydownListener);
    },

    setupDropdownWatchers() {
      this.setupDropdownWatcher(
        'showLocationDropdown',
        this.handleOutsideLocationClick
      );
      this.setupDropdownWatcher(
        'showBedroomsDropdown',
        this.handleOutsideClick
      );
    },

    cleanupEventListeners() {
      document.removeEventListener('mousedown', this._mousedownListener);
      document.removeEventListener('keydown', this._keydownListener);
      document.removeEventListener('click', this.handleOutsideClick);
      document.removeEventListener('click', this.handleOutsideLocationClick);
    },

    setupDropdownWatcher(dropdownProp, clickHandler) {
      this.$watch(dropdownProp, (isShown) => {
        if (isShown) {
          this.$nextTick(() =>
            document.addEventListener('click', clickHandler)
          );
        } else {
          document.removeEventListener('click', clickHandler);
        }
      });
    },
    filterLocationsByBedroom(locations) {
      let filteredLocations = locations;

      // Filter out locations with 0 total properties if the flag is enabled
      if (
        typeof window.__HERO_V2_HIDE_ZERO_COUNT_BEDROOMS !== 'undefined' &&
        window.__HERO_V2_HIDE_ZERO_COUNT_BEDROOMS
      ) {
        filteredLocations = filteredLocations.filter((loc) => loc.total > 0);
      }

      // Filter by selected bedroom if one is selected
      if (!this.selectedBedroom || !this.selectedBedroom.id) {
        return filteredLocations;
      }

      // For spare rooms, we don't have count data, so return all locations
      if (this.selectedBedroom.id === 'spare-rooms') {
        return filteredLocations;
      }

      if (
        typeof window.__HERO_V2_HIDE_ZERO_COUNT_BEDROOMS !== 'undefined' &&
        !window.__HERO_V2_HIDE_ZERO_COUNT_BEDROOMS
      ) {
        return filteredLocations;
      }

      return filteredLocations.filter((loc) =>
        this.locationHasBedroom(loc, this.selectedBedroom.id)
      );
    },
    locationHasBedroom(location, bedroomId) {
      try {
        if (!location || typeof location !== 'object') return false;
        if (!bedroomId) return false;
        if (!location.by_bedrooms || typeof location.by_bedrooms !== 'object')
          return false;

        const key = bedroomId === 'spare-rooms' ? 'spare_rooms' : bedroomId;
        const count = location.by_bedrooms[key];
        return typeof count === 'number' && count > 0;
      } catch (error) {
        return false;
      }
    },
    getLocationPropertyCount(location) {
      try {
        if (!location || typeof location !== 'object') return 0;

        // If no bedroom is selected, return total
        if (!this.selectedBedroom || !this.selectedBedroom.id) {
          return location.total || 0;
        }

        // For spare rooms, we don't have count data, return total as fallback
        if (this.selectedBedroom.id === 'spare-rooms') {
          if (
            !location.by_bedrooms ||
            typeof location.by_bedrooms !== 'object'
          ) {
            return 0;
          }

          const spareRoomsCount = location.by_bedrooms.spare_rooms;
          return typeof spareRoomsCount === 'number' ? spareRoomsCount : 0;
        }

        // If bedroom is selected, get specific bedroom count
        if (!location.by_bedrooms || typeof location.by_bedrooms !== 'object') {
          return 0;
        }

        const bedroomCount = location.by_bedrooms[this.selectedBedroom.id];
        return typeof bedroomCount === 'number' ? bedroomCount : 0;
      } catch (error) {
        // Silent fallback for production
        return 0;
      }
    },
    selectLocation(location) {
      this.isKeyboardNavigation = false;
      this.selectedLocation = location;
      this.searchQuery = location.name;
      this.showLocationDropdown = false;
      this.showingInitialSuggestions = false;
      this.locationError = false;
      this.announceForScreenReader(`Selected location: ${location.name}`);
    },
    selectBedroom(bedroom) {
      this.selectedBedroom = bedroom;
      this.selectedBedroomText = bedroom.name;
      this.showBedroomsDropdown = false;
      this.announceForScreenReader(`Selected ${bedroom.name}`);
      this.highlightedLocationIndex = -1;
    },
    clearLocationSearch(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      this.searchQuery = '';
      this.selectedLocation = null;
      this.locationError = false;
      this.showingInitialSuggestions = false;
      this.showLocationDropdown = true;
    },
    handleLocationFocus() {
      this.locationPlaceholder = 'Start typing';
      this.showingInitialSuggestions = false;
      this.showLocationDropdown = true;
      this.showBedroomsDropdown = false;
      this.isLocationInputFocused = true;
      if (this.lastInteractionWasKeyboard) {
        this.isKeyboardNavigation = true;
        this.highlightedLocationIndex = 0;
      } else {
        this.isKeyboardNavigation = false;
        this.highlightedLocationIndex = -1;
      }
    },
    handleLocationBlur() {
      setTimeout(() => {
        const activeElement = document.activeElement;
        const isInComponent =
          (this.$refs.bedroomsInput &&
            activeElement === this.$refs.bedroomsInput) ||
          (this.$refs.locationDropdown &&
            this.$refs.locationDropdown.contains(activeElement));
        if (!isInComponent) {
          this.closeLocationDropdown();
          if (!this.searchQuery)
            this.locationPlaceholder = 'Search for a city or nearby university';
        }
      }, 200);
    },
    closeLocationDropdown() {
      this.showLocationDropdown = false;
      this.showingInitialSuggestions = false;
      this.locationLabelFocused = false;
      this.highlightedLocationIndex = -1;
      this.isLocationInputFocused = false;
      if (!this.searchQuery)
        this.locationPlaceholder = 'Search for a city or nearby university';
      if (document.activeElement === this.$refs.locationInput)
        this.$refs.locationInput.blur();
      this.announceForScreenReader('Location dropdown closed');
    },
    handleOutsideLocationClick(event) {
      if (
        !this.elementContainsOrIs(event.target, [
          this.$refs.locationInput,
          this.$refs.locationDropdown,
          document.getElementById('location-select-2025'),
        ])
      ) {
        this.closeLocationDropdown();
      }
    },
    closeBedroomsDropdown() {
      this.showBedroomsDropdown = false;
      this.highlightedBedroomIndex = -1;
      this.isKeyboardNavigation = false;

      this.announceForScreenReader('Bedroom dropdown closed');
    },
    handleBedroomsBlur() {
      setTimeout(() => {
        const activeElement = document.activeElement;
        const isInComponent =
          (this.$refs.locationInput &&
            activeElement === this.$refs.locationInput) ||
          document
            .querySelector(
              '[data-uh="select-bedrooms-filter"] .tw-absolute.tw-z-50'
            )
            ?.contains(activeElement);
        if (!isInComponent) this.closeBedroomsDropdown();
      }, 200);
    },
    handleBedroomsMouseDown() {
      if (document.activeElement === this.$refs.locationInput)
        this.$refs.locationInput.blur();
      this.isKeyboardNavigation = false;
      this.showBedroomsDropdown = !this.showBedroomsDropdown;
      this.closeLocationDropdown();
      if (this.showBedroomsDropdown) {
        this.highlightedBedroomIndex = 0;
        this.$nextTick(() => {
          if (this.$refs.bedroomsInput) this.$refs.bedroomsInput.focus();
        });
      }
    },
    handleBedroomsFocus() {
      this.closeLocationDropdown();
      this.showBedroomsDropdown = true;
      if (this.selectedBedroom) {
        const selectedIndex = this.bedroomOptions.findIndex(
          (b) => b.id === this.selectedBedroom.id
        );
        this.highlightedBedroomIndex = selectedIndex >= 0 ? selectedIndex : 0;
      } else {
        this.highlightedBedroomIndex = 0;
      }
      this.isKeyboardNavigation = true;
    },
    handleOutsideClick(event) {
      if (
        !this.elementContainsOrIs(event.target, [
          this.$refs.bedroomsInput,
          document.querySelector(
            '[data-uh="select-bedrooms-filter"] .tw-absolute.tw-z-50'
          ),
          document.getElementById('bedrooms-select-2025'),
        ])
      ) {
        this.showBedroomsDropdown = false;
      }
    },
    handleMouseOver(index, type = 'location') {
      // Don't interfere with keyboard navigation, but still handle mouse interactions
      // This allows both input methods to coexist properly

      this.lastMouseOverTarget = { type, index };

      if (type === 'location') {
        this.mouseOverIndex = index;
        // Only update highlighted index if not actively keyboard navigating
        if (!this.isKeyboardNavigation) {
          this.highlightedLocationIndex = index;
        }
      } else if (type === 'bedrooms') {
        this.mouseOverBedroomIndex = index;
        // Only update highlighted index if not actively keyboard navigating
        if (!this.isKeyboardNavigation) {
          this.highlightedBedroomIndex = index;
        }
      }
    },
    handleBedroomMouseOver(index) {
      this.handleMouseOver(index, 'bedrooms');
    },
    handleMouseDown() {
      this.isKeyboardNavigation = false;
    },
    handleLocationKeyNavigation(direction, event) {
      this.isKeyboardNavigation = true;

      if (event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }

      if (!this.showLocationDropdown) {
        this.showLocationDropdown = true;
        return;
      }

      this.$nextTick(() => {
        const buttons = Array.from(
          this.$refs.locationDropdown.querySelectorAll('button[role="option"]')
        );

        if (!buttons.length) return;

        const currentIndex = this.highlightedLocationIndex;
        const currentButton = buttons[currentIndex] || buttons[0];
        const currentRect = currentButton.getBoundingClientRect();
        const currentX = currentRect.left;

        // Get the position of all the buttons in the dropdown.
        const buttonMap = buttons.map((btn, index) => {
          const rect = btn.getBoundingClientRect();

          return {
            index,
            x: rect.left,
            y: rect.top,
            element: btn,
          };
        });

        // Group the buttons into rows using their y position.
        const rows = [];

        buttonMap.forEach((btn) => {
          const existingRow = rows.find(
            (row) =>
              // `< 8` is to allow for any discrepancies in the y position.
              Math.abs(row.y - btn.y) < 8
          );

          if (existingRow) {
            existingRow.buttons.push(btn);
          } else {
            rows.push({ y: btn.y, buttons: [btn] });
          }
        });

        // Sort the rows by their vertical position.
        rows.sort((a, b) => a.y - b.y);

        // Find the row of the currently selected button.
        const currentRowIndex = rows.findIndex((row) =>
          row.buttons.some((b) => b.index === currentIndex)
        );

        let targetIndex = null;

        if (direction === 'up' || direction === 'down') {
          const isDown = direction === 'down';
          const nextRow = rows[currentRowIndex + (isDown ? 1 : -1)];

          if (nextRow) {
            const closest = nextRow.buttons.reduce((prev, curr) =>
              Math.abs(curr.x - currentX) < Math.abs(prev.x - currentX)
                ? curr
                : prev
            );

            targetIndex = closest.index;
          }
        } else if (direction === 'left' || direction === 'right') {
          const delta = direction === 'right' ? 1 : -1;
          const newIndex =
            (currentIndex + delta + buttons.length) % buttons.length;
          targetIndex = newIndex;
        }

        if (targetIndex != null) {
          this.highlightedLocationIndex = targetIndex;
          const item = buttonMap.find((b) => b.index === targetIndex);

          if (item?.element?.innerText) {
            this.announceForScreenReader(
              `Navigating: ${item.element.innerText.trim()}`
            );
          }

          this.scrollToHighlightedItem('location', targetIndex);

          if (this.$refs.locationInput) {
            this.$refs.locationInput.focus();
          }
        }
      });
    },

    handleBedroomsKeyNavigation(direction, event) {
      this.isKeyboardNavigation = true;
      if (event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }

      if (!this.showBedroomsDropdown) {
        this.showBedroomsDropdown = true;
        return;
      }

      const { items, currentIndex, maxIndex } =
        this.getNavigationInfo('bedrooms');
      if (maxIndex < 0) return;

      if (currentIndex === -1 && direction === 'down') {
        this.highlightedBedroomIndex = 0;
        if (items[0]) {
          this.announceForScreenReader(`Navigating: ${items[0].name}`);
        }
        this.$nextTick(() => this.scrollToHighlightedItem('bedrooms', 0));
        return;
      }

      const newIndex =
        direction === 'down'
          ? currentIndex < maxIndex
            ? currentIndex + 1
            : 0
          : currentIndex > 0
          ? currentIndex - 1
          : maxIndex;

      this.highlightedBedroomIndex = newIndex;
      if (items[newIndex]) {
        this.announceForScreenReader(`Navigating: ${items[newIndex].name}`);
      }

      this.$nextTick(() => {
        this.scrollToHighlightedItem('bedrooms', newIndex);
        if (this.$refs.bedroomsInput) {
          this.$refs.bedroomsInput.focus();
        }
      });
    },

    scrollToHighlightedItem(dropdownType, index) {
      if (dropdownType === 'bedrooms' && this.showBedroomsDropdown) {
        const dropdown = document.getElementById('bedrooms-listbox');
        const option =
          document.getElementById(`bedroom-option-${index}`) ||
          document.getElementById('bedroom-option-highlighted');
        if (dropdown && option) {
          const dropdownRect = dropdown.getBoundingClientRect();
          const optionRect = option.getBoundingClientRect();
          const isNotFullyVisible =
            optionRect.top < dropdownRect.top ||
            optionRect.bottom > dropdownRect.bottom;
          if (isNotFullyVisible) {
            if (optionRect.bottom > dropdownRect.bottom) {
              const scrollDown = optionRect.bottom - dropdownRect.bottom + 18;
              dropdown.scrollTop += scrollDown;
            } else if (optionRect.top < dropdownRect.top) {
              const scrollUp = dropdownRect.top - optionRect.top + 18;
              dropdown.scrollTop -= scrollUp;
            }
          }
        }
      }
    },

    getNavigationInfo(dropdownType) {
      if (dropdownType === 'location') {
        const items = this.navigableLocations;
        return {
          items,
          currentIndex: this.highlightedLocationIndex,
          maxIndex: items.length - 1,
        };
      } else {
        return {
          items: this.bedroomOptions,
          currentIndex: this.highlightedBedroomIndex,
          maxIndex: this.bedroomOptions.length - 1,
        };
      }
    },
    selectHighlightedLocation(event) {
      if (event) event.preventDefault();
      if (!this.showLocationDropdown) return;
      const location = this.getLocationFromHighlightIndex();
      if (location) {
        this.selectLocation(location);
        this.$nextTick(() => {
          if (this.$refs.locationInput) this.$refs.locationInput.focus();
        });
      } else if (
        this.searchQuery &&
        this.searchQuery.length >= 2 &&
        this.navigableLocations.length > 0
      ) {
        this.selectLocation(this.navigableLocations[0]);
        this.$nextTick(() => {
          if (this.$refs.locationInput) this.$refs.locationInput.focus();
        });
      }
    },
    selectHighlightedBedroom() {
      if (this.showBedroomsDropdown && this.highlightedBedroomIndex >= 0)
        this.selectBedroom(this.bedroomOptions[this.highlightedBedroomIndex]);
    },
    getLocationFromHighlightIndex() {
      if (this.highlightedLocationIndex < 0) return null;
      return this.navigableLocations[this.highlightedLocationIndex] || null;
    },
    search() {
      if (!this.selectedLocation) {
        this.showLocationError();

        if (this.$refs.locationInput) {
          setTimeout(() => this.$refs.locationInput.focus(), 0);
        }

        return;
      }

      // Navigate immediately with homepage search flag
      window.location.href = this.buildSearchUrl();
    },
    showLocationError() {
      document
        .querySelector('.home-find-uni-cities-input')
        .classList.add('text-red');
      this.locationError = true;
    },
    buildSearchUrl() {
      if (!this.selectedLocation) return '/';

      const isCity = this.selectedLocation.type?.toLowerCase() === 'city';
      let queryParams = [];

      // Handle spare rooms selection
      if (this.selectedBedroom?.id === 'spare-rooms') {
        queryParams.push('type=spare-rooms');
      } else if (this.selectedBedroom?.id) {
        // Regular bedroom count
        queryParams.push(`bedrooms=${this.selectedBedroom.id}`);
      }

      // Handle private halls
      if (this.slug === 'private-halls') {
        queryParams.push('type=private-halls');
      }

      if (this.slug === 'spare-rooms') {
        queryParams.push('type=spare-rooms');
      }

      // Add homepage search flag
      queryParams.push('hs=1');

      const queryString =
        queryParams.length > 0 ? `?${queryParams.join('&')}` : '';

      let url = isCity
        ? `/student-accommodation/${this.selectedLocation.slug}${queryString}`
        : `/student-accommodation/${this.selectedLocation.city}/near-${this.selectedLocation.slug}${queryString}`;

      return url;
    },
    getHighlightedParts(text, query) {
      try {
        // Bulletproof input validation
        if (!text || typeof text !== 'string') {
          return [{ text: text || '', highlight: false }];
        }
        if (!query || typeof query !== 'string' || query.length < 2) {
          return [{ text, highlight: false }];
        }

        const result = [];
        let lastIndex = 0;
        let match;
        const regex = new RegExp(
          query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
          'gi'
        );
        while ((match = regex.exec(text))) {
          if (match.index > lastIndex) {
            result.push({
              text: text.slice(lastIndex, match.index),
              highlight: false,
            });
          }
          result.push({ text: match[0], highlight: true });
          lastIndex = match.index + match[0].length;
        }
        if (lastIndex < text.length) {
          result.push({ text: text.slice(lastIndex), highlight: false });
        }
        return result;
      } catch (error) {
        // Graceful fallback for highlighting errors
        return [{ text: text || '', highlight: false }];
      }
    },
    elementContainsOrIs(target, elements) {
      return elements.some(
        (el) => el && (el === target || el.contains(target))
      );
    },
    announceForScreenReader(message) {
      const announcement = document.createElement('div');
      announcement.setAttribute('aria-live', 'polite');
      announcement.setAttribute('class', 'sr-only');
      announcement.textContent = message;
      document.body.appendChild(announcement);
      setTimeout(() => document.body.removeChild(announcement), 1000);
    },
  },
};
</script>
